import React from "react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";

const DEFAULT_SIZES = [25, 50, 100, 200, 500];

/**
 * Shared table-footer pagination bar — page-size select + prev/next.
 * Purely presentational; the page owns `page`/`pageSize` state and slices
 * its own rows (client-side pagination over an already-loaded list).
 */
export default function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_SIZES,
  testId = "pagination",
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  return (
    <div
      className="flex items-center justify-between gap-4 flex-wrap px-4 py-3 border-t border-slate-200 bg-slate-50/60"
      data-testid={testId}
    >
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <span>Rows per page</span>
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          className="h-8 pl-2 pr-6 rounded-md border border-slate-200 bg-white text-xs focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
          data-testid={`${testId}-size`}
        >
          {pageSizeOptions.map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-3 text-xs text-slate-500">
        <span data-testid={`${testId}-range`}>
          {total === 0 ? "0 of 0" : `${start}–${end} of ${total}`}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`${testId}-prev`}
          >
            <CaretLeft size={14} weight="bold" />
          </button>
          <span className="px-2 tabular-nums" data-testid={`${testId}-page`}>
            {page} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`${testId}-next`}
          >
            <CaretRight size={14} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}
